<template>
  <div class="filter-row">
    <feature-group
      v-if="rowFilters.featureGroup"
      :expanderVisible="isExpanderVisible('featureGroup')"
      @toggle="toggleExpander('featureGroup')"
      :currentGroup="filters?.featureGroup"
      :mode="filters.mode"
    ></feature-group>
    <location
      v-if="isFilterVisible('location')"
      :expanderVisible="isExpanderVisible('location')"
      :locationFilters="locationFilters"
      @toggle="toggleExpander('location')"
    ></location>
    <price-range
      v-if="isFilterVisible('priceMin')"
      :expanderVisible="isExpanderVisible('priceMin')"
      :currentMin="filters?.priceMin"
      :currentMax="filters?.priceMax"
      @toggle="toggleExpander('priceMin')"
    ></price-range>
    <rent
      v-if="isFilterVisible('rentMin')"
      :expanderVisible="isExpanderVisible('rentMin')"
      :currentMin="filters?.rentMin"
      :currentMax="filters?.rentMax"
      @toggle="toggleExpander('rentMin')"
    ></rent>
    <area-range
      key="livingArea"
      v-if="isFilterVisible('livingAreaMin')"
      :expanderVisible="isExpanderVisible('livingAreaMin')"
      @toggle="toggleExpander('livingAreaMin')"
      filterName="livingArea"
      :label="areaRangeLabel"
      :step="1"
      :limitMin="config.FILTER_LIMITS.AREA_MIN"
      :limitMax="config.FILTER_LIMITS.AREA_MAX"
      :currentMin="filters?.livingAreaMin"
      :currentMax="filters?.livingAreaMax"
    ></area-range>
    <area-range
      key="siteArea"
      v-if="isFilterVisible('siteAreaMin')"
      :expanderVisible="isExpanderVisible('siteAreaMin')"
      @toggle="toggleExpander('siteAreaMin')"
      filterName="siteArea"
      :label="areaRangeLabel"
      :step="100"
      :limitMin="config.FILTER_LIMITS.SITE_AREA_MIN"
      :limitMax="config.FILTER_LIMITS.SITE_AREA_MAX"
      :currentMin="filters?.siteAreaMin"
      :currentMax="filters?.siteAreaMax"
    ></area-range>
    <area-range
      key="totalArea"
      v-if="isFilterVisible('totalAreaMin')"
      :expanderVisible="isExpanderVisible('totalAreaMin')"
      @toggle="toggleExpander('totalAreaMin')"
      filterName="totalArea"
      :label="areaRangeLabel"
      :step="25"
      :limitMin="config.FILTER_LIMITS.TOTAL_AREA_MIN"
      :limitMax="config.FILTER_LIMITS.TOTAL_AREA_MAX"
      :currentMin="filters?.totalAreaMin"
      :currentMax="filters?.totalAreaMax"
    ></area-range>
    <number-of-rooms
      v-if="isFilterVisible('numberOfRooms')"
      :expanderVisible="isExpanderVisible('numberOfRooms')"
      @toggle="toggleExpander('numberOfRooms')"
      :numberOfRooms="filters?.numberOfRooms"
    ></number-of-rooms>
    <listing-types
      v-if="isFilterVisible('listingTypes')"
      :expanderVisible="isExpanderVisible('listingTypes')"
      @toggle="toggleExpander('listingTypes')"
      :feature-group="filters?.featureGroup"
      :selected-types="filters?.type"
    ></listing-types>
    <extra-features
      v-if="isFilterVisible('extraFeatures')"
      :expanderVisible="isExpanderVisible('extraFeatures')"
      @toggle="toggleExpander('extraFeatures')"
      :feature-group="filters?.featureGroup"
      :selected-features="filters?.feature"
    ></extra-features>
    <year-range
      v-if="isFilterVisible('yearMin')"
      :expanderVisible="isExpanderVisible('yearMin')"
      :currentMin="filters?.yearMin"
      :currentMax="filters?.yearMax"
      @toggle="toggleExpander('yearMin')"
    ></year-range>
    <condition
      v-if="isFilterVisible('condition')"
      :expanderVisible="isExpanderVisible('condition')"
      @toggle="toggleExpander('condition')"
      :selectedConditions="filters?.condition"
    ></condition>
    <property-id
      v-if="isFilterVisible('propertyId')"
      :expanderVisible="isExpanderVisible('propertyId')"
      @toggle="toggleExpander('propertyId')"
    ></property-id>
    <more-filters
      :rowFilters="rowFilters"
      :filters="filters"
      :expanderVisible="isExpanderVisible('moreFilters')"
      @toggle="toggleExpander('moreFilters')"
    ></more-filters>
    <button class="clear-filters" @click.prevent="clearFilters()">Tyhjennä hakuehdot</button>
  </div>
</template>

<script lang="ts">
import config from '@/config';
import ListingTypes from './listing-types.vue';
import Location from './location.vue';
import PriceRange from './price-range.vue';
import Rent from './rent.vue';
import NumberOfRooms from './number-of-rooms.vue';
import Condition from './condition.vue';
import FeatureGroup from './feature-group.vue';
import ExtraFeatures from './extra-features.vue';
import YearRange from './year-range.vue';
import PropertyId from './property-id.vue';
import AreaRange from './area-range.vue';
import Showings from './showings.vue';
import SiteOwnership from './site-ownership.vue';
import searchUtils from '@/app/utils/search-utils';
import Expander from '../expander/expander.vue';
import MoreFilters from './more-filters.vue';
import { getFeatureGroupById } from '@/config/featureGroups';
import type { FeatureGroupFiltersType, FilterViewType } from '@/config/featureGroups';
import { getAreaRangeLabel } from '@/utils/texts';
import type { FiltersType } from '@/types/schemaTypes';
import { defineComponent } from 'vue';
import type { PropType } from 'vue';

export default defineComponent({
  name: 'filter-row',
  props: {
    rowFilters: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object as PropType<FiltersType>,
      required: true,
    },
  },
  data: () => ({
    config,
    currentOpenExpander: '',
  }),
  computed: {
    hasActiveFilters() {
      return searchUtils.hasActiveUserFilters(this.filters);
    },
    locationFilters() {
      return searchUtils.getLocationFilters(this.filters);
    },
    featureGroupFilters() {
      return getFeatureGroupById(this.filters?.featureGroup)?.filters[
        this.filters?.mode as keyof FeatureGroupFiltersType
      ];
    },
    areaRangeLabel() {
      return getAreaRangeLabel(this.filters?.featureGroup);
    },
  },
  methods: {
    toggleExpander(expander: string) {
      this.currentOpenExpander = this.currentOpenExpander === expander ? '' : expander;
    },
    isExpanderVisible(expander: string) {
      return this.currentOpenExpander === expander;
    },
    isFilterVisible(filter: FilterViewType) {
      return this.rowFilters[filter] && this.featureGroupFilters?.includes(filter);
    },
    clearFilters() {
      searchUtils.removeAllFilters(this.$router, this.$route);
    },
    handleEscKey(event: KeyboardEvent) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this.currentOpenExpander = '';
      }
    },
  },
  mounted() {
    this.$bus.$on(
      'collapseExpanders',
      () => {
        this.currentOpenExpander = '';
      },
      this
    );

    window.addEventListener('keydown', this.handleEscKey);
  },
  components: {
    YearRange,
    ExtraFeatures,
    ListingTypes,
    Location,
    PriceRange,
    Rent,
    NumberOfRooms,
    Condition,
    PropertyId,
    FeatureGroup,
    AreaRange,
    Showings,
    SiteOwnership,
    Expander,
    MoreFilters,
  },
});
</script>
